import * as React from 'react'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { useFormikContext } from 'formik'
import { Gender, PersonNameDto } from '../../../config/contactConfig'
import Fieldset from '../Fieldset'
import LegacyAddressField from '../../Field/Address/LegacyAddressField'
import FormikTextField from '../../../components/Formik/TextField/FormikTextField'
import FormikPhoneNumberField from '../../Formik/FormikPhoneNumberField'
import PartyForm, { BasePartyDto, PartyProps, PartyStatusDropdown } from './PartyForm'
import FormikDropdown from '../../../components/Formik/Dropdown/FormikDropdown'
import { DefaultButton, Stack, StackItem } from '@fluentui/react'
import useViewport, { Viewport } from '../../../hooks/useViewport'
import FormContainer from '../../../components/Form/FormContainer'
import { EntityType } from '../Accounting/EntityField'
import * as Yup from 'yup'
import { LocalizedValueSetting, ModuleType } from '../../../context/GlobalContext'
import useModules from '../../../hooks/module/useModules'
import { useTranslation } from 'react-i18next'
import FormikField from '../../Formik/FormikField'
import LocalDatePicker, { LocalDatePickerProps } from '../../DatePicker/LocalDatePicker'
import EnumCombobox, { EnumComboboxProps } from '../../Combobox/EnumCombobox'
import { useSettings } from '../../../hooks/useSettings'
import CountryCombobox, { CountryComboboxProps } from '../../Combobox/i18n/CountryCombobox'
import type { i18n } from 'i18next'
import AttributesList from '../Attributes/AttributesList'
import {
    AttributeContextType,
    AttributeDefinitionDto,
    AttributeViewType,
    EntityAttributeType,
    getAttributeValidationSchema,
} from '../../../hooks/useAttributes'
import FormikComboboxField from '../../Formik/FormikComboboxField'
import FormikInputField from '../../Formik/FormikInputField'
import MultiUnitDimensionFields from '../Accounting/MultiUnitDimensionFields'
import { LegalPersonMutationDto, useStyles } from './LegalPersonForm'
import { PartyTierDto } from '../../../config/finance/financeConfig'
import { PartyTierListItem } from '../Tier/PartyTierListItem'
import TierList from '../Tier/TierList'
import PartyTierFormContainer, { partyTierValidationSchema } from '../Tier/PartyTierFormContainer'
import { DateTime } from 'luxon'
import { formatMultipleEmailAddresses, yupEmailRegex } from '../../../helpers/regexHelper'

enum IdentificationType {
    Passport = 'PASSPORT',
    IdCard = 'ID_CARD',
    DriversLicense = 'DRIVERS_LICENSE',
    Other = 'OTHER',
}

export type NaturalPersonMutationDto = BasePartyDto & {
    gender: Gender
    name: PersonNameDto
    salutation: string
    attentionOf: string
    phoneMobile: string
    dateOfBirth: string
    identificationNumber: string
    identificationPersonalNumber: string
    identificationType: IdentificationType
    identificationDateOfIssue: string
    identificationDateOfExpiry: string
    identificationAuthority: string
    identificationPlaceOfIssue: string
    identificationCountryOfIssue: string
}

const getValidationSchema = (translate: (key: string) => string, attributeDefinitions: AttributeDefinitionDto[]): Yup.ObjectSchema<any> => {
    return Yup.object().shape({
        gender: Yup.string().label(translate('party.edit.person.gender')).required(),
        name: Yup.object().shape({
            last: Yup.string().label(translate('party.edit.person.last')).required(),
        }),
        email: Yup.string()
            .nullable()
            .test({
                message: translate('base.form.email.warning'),
                test: values =>
                    !values ||
                    formatMultipleEmailAddresses(values)?.every(value => {
                        const result = value?.trim().match(yupEmailRegex)
                        return result?.length > 0
                    }),
            })
            .label(translate('party.edit.contact.email')),
        addressCountry: Yup.string()
            .nullable()
            .test({
                test: (value, context) =>
                    !!value?.trim() ||
                    !!value ===
                        !!(
                            context.parent.addressStreet?.trim() ||
                            context.parent.addressNumber?.trim() ||
                            context.parent.addressPostalCode?.trim() ||
                            context.parent.addressLocality?.trim()
                        ),
            })
            .label(translate('party.edit.address.country')),
        attributes: getAttributeValidationSchema(translate, attributeDefinitions),
    })
}

export function getDefaultSettingValue(
    person: NaturalPersonMutationDto,
    i18n: i18n,
    setting: LocalizedValueSetting<string, LocalizedValueSetting<Gender>[]>[],
) {
    let settingValue = setting?.find(setting => setting.key === i18n.language)?.value?.find(setting => setting.key === person.gender)?.value || ''

    let insertionUpper: string = person.name?.insertion
    if (insertionUpper?.length > 1) {
        insertionUpper = insertionUpper?.charAt(0).toUpperCase() + insertionUpper?.slice(1)
    } else if (insertionUpper?.length === 1) {
        insertionUpper = insertionUpper?.toUpperCase().toWellFormed()
    }

    settingValue = settingValue.replaceAll('${gender}', person.gender || '')
    settingValue = settingValue.replaceAll('${initials}', person.name?.initials || '')
    settingValue = settingValue.replaceAll('${first}', person.name?.first || '')
    settingValue = settingValue.replaceAll('${given}', person.name?.given || '')
    settingValue = settingValue.replaceAll('${insertion|upper}', insertionUpper || '')
    settingValue = settingValue.replaceAll('${insertion}', person.name?.insertion || '')
    settingValue = settingValue.replaceAll('${last}', person.name.last || '')

    settingValue = settingValue.replaceAll(/\s{2,}/gm, ' ')
    return settingValue.trim()
}

function Container(props: PartyProps<NaturalPersonMutationDto>): ReactElement {
    const modules = useModules()
    const { settings } = useSettings()
    const styles = useStyles()
    const { i18n: i18n, t: translate } = useTranslation()

    const { values, initialValues, setFieldValue, submitForm, isSubmitting } = useFormikContext<NaturalPersonMutationDto>()

    const [hasCustomAttentionOf, setHasCustomAttentionOf] = useState<boolean>(undefined)
    const [hasCustomSalutation, setHasCustomSalutation] = useState<boolean>(undefined)

    const context = useFormikContext<NaturalPersonMutationDto>()
    const { isViewport, getGap, viewport } = useViewport()
    const isHorizontal = useMemo<boolean>(() => isViewport('>=', Viewport.Large), [viewport])

    function checkCustomAttentionOf(_values: NaturalPersonMutationDto): boolean {
        return (
            _values.attentionOf?.trim().length > 0 &&
            _values.attentionOf?.trim() !== getDefaultSettingValue(_values, i18n, settings?.partyAttentionOf)
        )
    }

    function checkCustomSalutation(_values: NaturalPersonMutationDto): boolean {
        return (
            _values.salutation?.trim().length > 0 && _values.salutation?.trim() !== getDefaultSettingValue(_values, i18n, settings?.partySalutation)
        )
    }

    useEffect(() => {
        // Set custom attention of state when initial value does not match default
        setHasCustomAttentionOf(checkCustomAttentionOf(initialValues))
    }, [settings])

    useEffect(() => {
        // Set custom salutation state when initial value does not match default
        setHasCustomSalutation(checkCustomSalutation(initialValues))
    }, [settings])

    useEffect(() => {
        if (hasCustomAttentionOf !== undefined) {
            // Build the default attention of string (how it should be based on filled in fields)
            const defaultAttentionOf: string = getDefaultSettingValue(values, i18n, settings?.partyAttentionOf)
            // Set the attention of field if we didn't enter a custom attention of or no old attention of has been set
            if (!hasCustomAttentionOf) {
                setFieldValue('attentionOf', defaultAttentionOf)
            }
        }
    }, [values.name, values.gender, i18n.language, hasCustomAttentionOf])

    useEffect(() => {
        if (hasCustomSalutation !== undefined) {
            // Build the default salutation string (how it should be based on filled in fields)
            const defaultSalutation: string = getDefaultSettingValue(values, i18n, settings?.partySalutation)
            // Set the salutation field if we didn't enter a custom salutation or no old salutation has been set
            if (!hasCustomSalutation) {
                setFieldValue('salutation', defaultSalutation)
            }
        }
    }, [values.name, values.gender, i18n.language, hasCustomSalutation])

    // noinspection JSUnusedGlobalSymbols
    return (
        <FormContainer>
            <Fieldset legend={translate('party.edit.person.label')}>
                <Stack horizontal={isHorizontal} tokens={{ childrenGap: getGap() }}>
                    <StackItem>
                        <FormikInputField<any> labelKey="party.edit.person.given" fieldName="name.given" componentProps={{ autoFocus: true }} />
                    </StackItem>
                    <Stack grow horizontal tokens={{ childrenGap: getGap() }}>
                        <StackItem>
                            <FormikInputField<any> labelKey="party.edit.person.insertion" fieldName="name.insertion" />
                        </StackItem>
                        <StackItem grow>
                            <FormikInputField<any> required labelKey="party.edit.person.last" fieldName="name.last" />
                        </StackItem>
                    </Stack>
                </Stack>
                <Stack horizontal tokens={{ childrenGap: getGap() }}>
                    <StackItem>
                        <FormikInputField<any> labelKey="party.edit.person.initials" fieldName="name.initials" />
                    </StackItem>
                    <StackItem grow>
                        <FormikInputField<any> labelKey="party.edit.person.first" fieldName="name.first" />
                    </StackItem>
                </Stack>
                <FormikInputField<LegalPersonMutationDto> labelKey="party.edit.person.shortname" fieldName="shortName" />
                <Stack horizontal tokens={{ childrenGap: getGap() }}>
                    <StackItem grow>
                        <FormikComboboxField<NaturalPersonMutationDto, EnumComboboxProps<Gender>>
                            required
                            labelKey={'base.gender.label'}
                            fieldName={'gender'}
                            component={EnumCombobox}
                            componentProps={{
                                entities: Object.values(Gender),
                                labelKeyPrefix: 'base.gender',
                                clearable: false,
                            }}
                        />
                    </StackItem>
                    <StackItem grow>
                        <FormikField<LocalDatePickerProps, NaturalPersonMutationDto>
                            labelKey="party.edit.other.dateofbirth"
                            fieldName="dateOfBirth"
                            component={LocalDatePicker}
                            componentProps={{
                                isoValue: values.dateOfBirth,
                                onSelectDate: date => setFieldValue('dateOfBirth', date),
                            }}
                        />
                    </StackItem>
                </Stack>
                <Stack horizontal tokens={{ childrenGap: getGap() }}>
                    <StackItem grow>
                        <FormikInputField<NaturalPersonMutationDto>
                            labelKey="party.edit.person.salutation"
                            fieldName="salutation"
                            componentProps={{
                                onBlur: () => setHasCustomSalutation(checkCustomSalutation(values)),
                                onKeyUp: () => setHasCustomSalutation(checkCustomSalutation(values)),
                            }}
                        />
                    </StackItem>
                    <StackItem grow>
                        <FormikInputField<NaturalPersonMutationDto>
                            labelKey="party.edit.person.attentionOf"
                            fieldName="attentionOf"
                            componentProps={{
                                onBlur: () => setHasCustomAttentionOf(checkCustomAttentionOf(values)),
                                onKeyUp: () => setHasCustomAttentionOf(checkCustomAttentionOf(values)),
                            }}
                        />
                    </StackItem>
                </Stack>
            </Fieldset>

            <Fieldset legend={translate('party.edit.contact.label')}>
                <Stack horizontal tokens={{ childrenGap: getGap() }}>
                    <StackItem grow>
                        <FormikPhoneNumberField label={translate('party.edit.contact.phonemobile')} fieldName="phoneMobile" />
                    </StackItem>
                    <StackItem grow>
                        <FormikPhoneNumberField label={translate('party.edit.contact.phonelandline')} fieldName="phoneLandline" />
                    </StackItem>
                </Stack>
                <FormikInputField<NaturalPersonMutationDto>
                    labelKey="party.edit.contact.email"
                    fieldName="email"
                    componentProps={{
                        onBlur: e => {
                            const newValue = formatMultipleEmailAddresses(e.target.value)
                            setFieldValue('email', newValue?.join(';'))
                        },
                    }}
                />
            </Fieldset>

            <LegacyAddressField legend={translate('party.edit.address.label')} prefix="address" />

            {modules.has(ModuleType.Attributes) && (props.attributes.activeAttributeDefinitions.length > 0 || values.attributes?.length > 0) && (
                <Fieldset legend={translate('party.edit.attributes.label')} canToggle>
                    <FormContainer>
                        <AttributesList
                            {...props}
                            attributeDefinitions={props.attributes.attributeDefinitions}
                            entityType={EntityAttributeType.Party}
                            context={[AttributeContextType.NaturalPerson]}
                            viewType={AttributeViewType.Input}
                            orientation={isHorizontal ? 'horizontal' : 'vertical'}
                            entity={values}
                            entityId={values.id}
                            setFieldValue={setFieldValue}
                        />
                    </FormContainer>
                </Fieldset>
            )}

            <Fieldset legend={translate('party.edit.identification.label')} canToggle>
                <Stack horizontal={isHorizontal} tokens={{ childrenGap: getGap() }}>
                    <Stack grow horizontal tokens={{ childrenGap: getGap() }}>
                        <StackItem grow styles={{ root: { overflow: 'hidden' } }}>
                            <FormikTextField
                                context={context}
                                label={translate('party.edit.identification.identificationnumber')}
                                fieldName="identificationNumber"
                            />
                        </StackItem>
                        <StackItem grow styles={{ root: { overflow: 'hidden' } }}>
                            <FormikTextField
                                context={context}
                                label={translate('party.edit.identification.identificationpersonalnumber')}
                                fieldName="identificationPersonalNumber"
                            />
                        </StackItem>
                    </Stack>
                    <StackItem grow>
                        <FormikDropdown
                            context={context}
                            label={translate('party.edit.identification.identificationtype')}
                            fieldName="identificationType"
                            options={[
                                { key: IdentificationType.Passport, text: 'Paspoort' },
                                { key: IdentificationType.IdCard, text: 'ID kaart' },
                                { key: IdentificationType.DriversLicense, text: 'Rijbewijs' },
                                { key: IdentificationType.Other, text: 'Overig' },
                            ]}
                        />
                    </StackItem>
                </Stack>

                <Stack horizontal tokens={{ childrenGap: getGap() }}>
                    <StackItem grow>
                        <FormikField<LocalDatePickerProps, NaturalPersonMutationDto>
                            labelKey="party.edit.identification.identificationdateofissue"
                            fieldName="identificationDateOfIssue"
                            component={LocalDatePicker}
                            componentProps={{
                                isoValue: values.identificationDateOfIssue,
                                onSelectDate: date => setFieldValue('identificationDateOfIssue', date),
                            }}
                        />
                    </StackItem>
                    <StackItem grow>
                        <FormikField<LocalDatePickerProps, NaturalPersonMutationDto>
                            labelKey="party.edit.identification.identificationdateofexpiry"
                            fieldName="identificationDateOfExpiry"
                            component={LocalDatePicker}
                            componentProps={{
                                isoValue: values.identificationDateOfExpiry,
                                onSelectDate: date => setFieldValue('identificationDateOfExpiry', date),
                            }}
                        />
                    </StackItem>
                </Stack>
                <Stack horizontal={isHorizontal} tokens={{ childrenGap: getGap() }}>
                    <StackItem grow>
                        <FormikTextField
                            context={context}
                            label={translate('party.edit.identification.identificationauthority')}
                            fieldName="identificationAuthority"
                        />
                    </StackItem>
                    <Stack grow horizontal={isHorizontal} tokens={{ childrenGap: getGap() }}>
                        <StackItem grow>
                            <FormikTextField
                                context={context}
                                label={translate('party.edit.identification.identificationplaceofissue')}
                                fieldName="identificationPlaceOfIssue"
                            />
                        </StackItem>
                        <StackItem grow>
                            <FormikComboboxField<NaturalPersonMutationDto, CountryComboboxProps>
                                labelKey="party.edit.identification.identificationcountryofissue"
                                fieldName="identificationCountryOfIssue"
                                component={CountryCombobox}
                            />
                        </StackItem>
                    </Stack>
                </Stack>
            </Fieldset>

            {modules.has(ModuleType.Accounting) && (
                <Fieldset legend={translate('party.edit.tier.label')} canToggle>
                    <TierList<NaturalPersonMutationDto, PartyTierDto>
                        labelPrefixKey={'party.edit.tier'}
                        defaultTierValue={() => ({
                            type: 'PARTY_TIER',
                            validFrom: DateTime.now().startOf('month').toISODate(),
                            validTo: undefined,
                            salesRate: '0.00',
                            currency: settings.fileScaffold?.currency || 'EUR',
                            partyId: values.id,
                        })}
                        parentValues={values}
                        createListItem={(index: number, item: PartyTierDto, editTier) => (
                            <PartyTierListItem index={index} item={item} editTier={editTier} />
                        )}
                        createTierFormContainer={() => <PartyTierFormContainer />}
                        validationSchema={partyTierValidationSchema}
                    />
                </Fieldset>
            )}

            {modules.has(ModuleType.Accounting) && (
                <MultiUnitDimensionFields
                    styles={styles}
                    fieldName="accounting"
                    selectedEntityTypes={[EntityType.CostCenter, EntityType.CostUnit, EntityType.GLA, EntityType.Project, EntityType.Customer]}
                />
            )}

            <Fieldset legend={translate('party.edit.other.label')} canToggle>
                <PartyStatusDropdown />
            </Fieldset>

            <Stack horizontal tokens={{ childrenGap: getGap() }}>
                <StackItem>
                    <DefaultButton primary={true} text={translate('action.save')} disabled={isSubmitting} onClick={submitForm} />
                </StackItem>
                <StackItem>
                    <DefaultButton primary={false} text={translate('action.cancel')} onClick={props.goBack} />
                </StackItem>
            </Stack>
        </FormContainer>
    )
}

export default function NaturalPersonForm(props: PartyProps<NaturalPersonMutationDto>): ReactElement {
    const { t: translate } = useTranslation()

    return (
        <PartyForm<NaturalPersonMutationDto>
            {...props}
            formikProps={{ ...props.formikProps, validationSchema: getValidationSchema(translate, props.attributes.activeAttributeDefinitions) }}
        >
            <Container {...props} />
        </PartyForm>
    )
}
